import { Link, Stack, useRouter } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@passfirst/api/router";

import { layout, radius, space, textStyle } from "@passfirst/ui";

import { Button } from "../src/components/button";
import { ScreenAppbar } from "../src/components/screen-appbar";
import { TabScaffold } from "../src/tab-scaffold";
import { type Colors, useColors } from "../src/theme";
import { trpc } from "../src/trpc";
import { friendlyTrpcError } from "../src/trpc-error";
import { useEffectiveSession } from "../src/use-effective-session";
import { useResponsive } from "../src/use-responsive";
import {
  useSelectedVertical,
  verticalDisplayLabel,
} from "../src/use-selected-vertical";

interface EntitlementCheck {
  hasAccess: boolean;
  tier: "PAID" | "FREE" | "EXHAUSTED";
}

// Derived from the server's actual response shape via tRPC inference
// — no more `as unknown as` cast, and a router change here will trip
// the typechecker rather than silently drift.
type ReviewItem =
  inferRouterOutputs<AppRouter>["questions"]["reviewQueue"][number];

export default function Review(): JSX.Element {
  const router = useRouter();
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { isWide } = useResponsive();
  const session = useEffectiveSession();
  const [items, setItems] = useState<ReviewItem[] | null>(null);
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entitlement, setEntitlement] = useState<EntitlementCheck | null>(null);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const selected = useSelectedVertical();

  useEffect(() => {
    let cancelled = false;
    if (selected.isLoading) return;
    if (!selected.verticalSlug) {
      if (!cancelled)
        setError("No vertical selected. Pick one from the home page first.");
      return;
    }
    void (async () => {
      try {
        // Fetch entitlement + review queue in parallel. Review queue
        // intentionally lets EXHAUSTED users through server-side
        // (revisiting wrong answers is value they've already earned),
        // so the UI just needs to know the tier to choose the right
        // forward CTA — Practice for FREE/PAID, Unlock for EXHAUSTED.
        const [ent, queue] = await Promise.all([
          trpc.entitlement.check.query({
            verticalSlug: selected.verticalSlug!,
            jurisdictionCode: selected.jurisdictionCode ?? undefined,
          }),
          trpc.questions.reviewQueue.query({
            verticalSlug: selected.verticalSlug!,
            jurisdictionCode: selected.jurisdictionCode ?? undefined,
            limit: 10,
            windowDays: 7,
          }),
        ]);
        if (!cancelled) {
          setEntitlement(ent);
          setItems(queue);
        }
      } catch (err) {
        // Never render a raw tRPC code/message to the user (#10).
        if (!cancelled) setError(friendlyTrpcError(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected.isLoading, selected.verticalSlug, selected.jurisdictionCode]);

  function handleUnlock(): void {
    if (!selected.verticalSlug) return;
    // Route into /paywall so the user sees the tier choice + the
    // platform-correct checkout rail (Stripe on web, IAP on native).
    // Previously this called checkout.createSession inline — fine for
    // Stripe, broken on native where Apple/Google IAP is required.
    setCheckoutBusy(true);
    // Leave the busy flag set — it guards against double-taps until we
    // navigate away and this screen unmounts. Clearing it synchronously
    // here would negate the guard (state updates are async, so the button
    // would never actually appear disabled).
    router.push({
      pathname: "/paywall",
      params: {
        vertical: selected.verticalSlug,
        ...(selected.jurisdictionCode
          ? { jurisdiction: selected.jurisdictionCode }
          : {}),
      },
    });
  }

  const isExhausted = entitlement?.tier === "EXHAUSTED";

  // Nav chrome wrapper for the early-return states (loading/error/empty/
  // done) so the user is never stranded without the tab bar / sidebar (#17).
  const scaffold = (children: JSX.Element): JSX.Element => (
    <TabScaffold
      userLabel={session?.user?.email ?? "Account"}
      metaLabel={verticalDisplayLabel(selected)}
    >
      {children}
    </TabScaffold>
  );

  if (!items && !error) {
    return scaffold(
      <View style={styles.center} accessibilityLabel="Loading review queue">
        <ActivityIndicator />
      </View>,
    );
  }

  if (error) {
    return scaffold(
      <View style={styles.center}>
        <Text style={styles.error} accessibilityLiveRegion="polite">
          {error}
        </Text>
        <Link href="/" asChild>
          <Button label="Back home" accessibilityLabel="Back home" fullWidth />
        </Link>
      </View>,
    );
  }

  if (!items || items.length === 0) {
    return scaffold(
      <View style={styles.center}>
        <Text style={styles.title}>Nothing to review</Text>
        <Text style={styles.subtitle}>
          {isExhausted
            ? "You haven't missed a question in the last 7 days. Unlock the full pool to keep practicing fresh questions."
            : "You haven't missed a question in the last 7 days. Keep practicing — if anything trips you up, it'll show up here."}
        </Text>
        {isExhausted ? (
          <Button
            label="Unlock the full pool"
            onPress={handleUnlock}
            busy={checkoutBusy}
            disabled={checkoutBusy}
            accessibilityLabel="Unlock the full question pool"
            fullWidth
          />
        ) : (
          <Link href="/practice" asChild>
            <Button
              label="Practice"
              accessibilityLabel="Start practicing"
              fullWidth
            />
          </Link>
        )}
      </View>,
    );
  }

  if (index >= items.length) {
    return scaffold(
      <View style={styles.center}>
        <Text style={styles.title}>Reviewed {items.length} questions</Text>
        <Text style={styles.subtitle}>
          {isExhausted
            ? "You're caught up on the misses from your free trial. Unlock the full pool to keep practicing fresh questions."
            : "Now go practice — you'll see fresh questions on these topics first."}
        </Text>
        {isExhausted ? (
          <Button
            label="Unlock the full pool"
            onPress={handleUnlock}
            busy={checkoutBusy}
            disabled={checkoutBusy}
            accessibilityLabel="Unlock the full question pool"
            fullWidth
          />
        ) : (
          <Link href="/practice" asChild>
            <Button
              label="Practice"
              accessibilityLabel="Start practicing"
              fullWidth
            />
          </Link>
        )}
      </View>,
    );
  }

  const q = items[index]!;

  return (
    <TabScaffold
      userLabel={session?.user?.email ?? "Account"}
      metaLabel={verticalDisplayLabel(selected)}
    >
      <Stack.Screen options={{ title: "Review" }} />
      <View style={styles.outer}>
        {!isWide ? <ScreenAppbar title="Review" /> : null}
        <ScrollView contentContainerStyle={styles.scroll}>
        {isExhausted ? (
          <View style={styles.exhaustedBanner}>
            <Text style={styles.exhaustedBannerText}>
              You're reviewing past misses from your free trial. Unlock to
              keep practicing fresh questions.
            </Text>
            <Button
              label="Unlock"
              onPress={handleUnlock}
              busy={checkoutBusy}
              disabled={checkoutBusy}
              accessibilityLabel="Unlock the full question pool"
              size="sm"
              testID="review-banner-unlock"
            />
          </View>
        ) : null}

        <View style={styles.header}>
          <Text style={styles.progress}>
            {index + 1} of {items.length}
          </Text>
          <Text style={styles.topic}>{q.topicLabel}</Text>
        </View>

        <Text style={styles.stem}>{q.stem}</Text>

        <View
          style={styles.choices}
          accessibilityRole="list"
          accessibilityLabel="Answer choices"
        >
          {q.choices.map((c) => {
            const isCorrect = c.label === q.correctLabel;
            const wasYours = c.label === q.userAnswer;
            const a11ySuffix = !revealed
              ? wasYours
                ? ", your answer"
                : ""
              : isCorrect
                ? wasYours
                  ? ", your answer, correct"
                  : ", correct answer"
                : wasYours
                  ? ", your answer, incorrect"
                  : "";
            return (
              <View
                key={c.label}
                accessibilityLabel={`Choice ${c.label}: ${c.text}${a11ySuffix}`}
                style={[
                  styles.choice,
                  revealed && isCorrect && styles.choiceCorrect,
                  revealed && wasYours && !isCorrect && styles.choiceWrong,
                ]}
              >
                <Text style={styles.choiceLabel}>{c.label}.</Text>
                <Text style={styles.choiceText}>{c.text}</Text>
                {revealed && wasYours && (
                  <Text style={styles.choiceTag}>your answer</Text>
                )}
              </View>
            );
          })}
        </View>

        {revealed ? (
          <View style={styles.explanation}>
            <Text style={styles.explanationHeader}>
              Correct answer: {q.correctLabel}
            </Text>
            <Text style={styles.explanationBody}>{q.explanation}</Text>
          </View>
        ) : null}

        <View style={styles.footer}>
          {!revealed ? (
            <Button
              label="Reveal answer"
              onPress={() => setRevealed(true)}
              accessibilityLabel="Reveal correct answer and explanation"
              fullWidth
            />
          ) : (
            <Button
              label={index + 1 >= items.length ? "Done" : "Next"}
              onPress={() => {
                setRevealed(false);
                setIndex((i) => i + 1);
              }}
              accessibilityLabel={
                index + 1 >= items.length
                  ? "Done with review"
                  : "Next question to review"
              }
              fullWidth
            />
          )}
        </View>
        </ScrollView>
      </View>
    </TabScaffold>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
  outer: { flex: 1, backgroundColor: colors.background },
  center: {
    flex: 1,
    backgroundColor: colors.background,
    alignItems: "center",
    justifyContent: "center",
    padding: space[6],
    gap: space[3],
  },
  scroll: {
    padding: space[6],
    gap: space[4],
    maxWidth: layout.contentMaxWidth,
    alignSelf: "center",
    width: "100%",
  },
  header: { flexDirection: "row", justifyContent: "space-between" },
  progress: { ...textStyle.caption, color: colors.textMuted },
  topic: { ...textStyle.label, color: colors.accentSoftText },
  title: { ...textStyle.h2, fontSize: 22, color: colors.textPrimary, textAlign: "center" },
  subtitle: { ...textStyle.bodySmall, color: colors.textMuted, textAlign: "center" },
  stem: { ...textStyle.body, fontSize: 18, lineHeight: 26, color: colors.textPrimary },
  choices: { gap: space[2] },
  choice: {
    flexDirection: "row",
    gap: space[2],
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: radius.md,
    padding: space[4],
    backgroundColor: colors.surface,
    alignItems: "center",
  },
  choiceCorrect: {
    borderColor: colors.borderCorrect,
    backgroundColor: colors.surfaceCorrect,
  },
  choiceWrong: {
    borderColor: colors.borderWrong,
    backgroundColor: colors.surfaceWrong,
  },
  choiceLabel: { ...textStyle.buttonPrimary, color: colors.textPrimary, minWidth: 24 },
  choiceText: { ...textStyle.body, flex: 1, fontSize: 15, lineHeight: 22 },
  choiceTag: { ...textStyle.caption, color: colors.textMuted, fontSize: 11, fontStyle: "italic" },
  explanation: {
    padding: space[4],
    borderRadius: radius.md,
    backgroundColor: colors.surfaceMuted,
    gap: space[1],
  },
  explanationHeader: { ...textStyle.label, fontSize: 15, color: colors.textCorrect },
  explanationBody: { ...textStyle.bodySmall, color: colors.textSecondary, lineHeight: 21 },
  footer: { marginTop: space[3] },
  error: { ...textStyle.bodySmall, color: colors.error, textAlign: "center" },
  // Soft amber banner — informational, not pushy. Lives at the top of
  // the review session for EXHAUSTED users so they know why "Practice"
  // isn't an option from here.
  exhaustedBanner: {
    flexDirection: "row",
    gap: space[3],
    alignItems: "center",
    backgroundColor: colors.surfaceCaution,
    borderRadius: radius.md,
    padding: space[4],
  },
  exhaustedBannerText: {
    ...textStyle.bodySmall,
    color: colors.textPrimary,
    flex: 1,
  },
  });
}
